import { convertPrice } from 'helpers/utils';
import { Minus, Plus, X } from 'lucide-react';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import { ShoppingCartHook } from 'slices/shoppingCart/hook';
import {
  addToCart,
  IShoppingCart,
  removeFromCart,
} from 'slices/shoppingCart/reducer';
import Drawer from './Components/Drawer';
import { EcommerceHook } from 'slices/ecommerce/hook';

const CartDrawer = ({ show, handleDrawer }: any) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { carts } = ShoppingCartHook();
  const { exChangeRate } = EcommerceHook();

  const subTotal = useMemo(() => {
    return carts.reduce((prev, curr) => prev + curr.price * curr.quantity, 0);
  }, [carts]);

  const counts = useMemo(() => {
    return carts.reduce((pre, cur) => {
      return pre + cur.quantity;
    }, 0);
  }, [carts]);

  const updateQuantity = (id: string, properties: string[], delta: number) => {
    const currentProduct = carts.find(
      (cart) =>
        cart.id === id &&
        JSON.stringify(cart.properties) === JSON.stringify(properties)
    );
    if (!currentProduct) return;
    dispatch(
      addToCart({
        ...currentProduct,
        quantity: delta,
      })
    );
  };

  const countUP = (id: string, properties: string[]) => {
    updateQuantity(id, properties, +1);
  };

  const countDown = (id: string, properties: string[]) => {
    updateQuantity(id, properties, -1);
  };

  const removeItem = (id: string, properties: string[]) => {
    dispatch(removeFromCart({ id, properties }));
  };

  return (
    <React.Fragment>
      <Drawer
        show={show}
        onHide={handleDrawer}
        id="cartSidePenal"
        drawer-end="true"
        className="fixed inset-y-0 flex flex-col w-full transition-transform duration-300 ease-in-out transform bg-white shadow dark:bg-zink-600 ltr:right-0 rtl:left-0 md:w-96 z-drawer"
      >
        <div className="flex items-center justify-between p-4 border-b border-slate-200 dark:border-zink-500">
          <div className="grow">
            <h5 className="mb-0 text-16">
              {t('Shopping Cart')}{' '}
              <span className="inline-flex items-center justify-center size-5 ml-1 text-[11px] font-medium border rounded-full text-white bg-common border-common">
                {counts}
              </span>
            </h5>
          </div>
          <div className="shrink-0">
            <Drawer.Header
              data-drawer-close="cartSidePenal"
              className="transition-all duration-150 ease-linear text-slate-500 hover:text-slate-800"
            >
              <X className="size-4"></X>
            </Drawer.Header>
          </div>
        </div>
        {Boolean(counts) ? (
          <div>
            <div className="p-4 overflow-y-auto product-list max-h-[calc(100vh-174px)]">
              <div className="flex flex-col gap-4">
                {(carts || [])?.map((item: IShoppingCart, index: number) => (
                  <div
                    key={index}
                    className="flex gap-2 product"
                    id={`product${item.id}`}
                  >
                    <div className="flex items-center justify-center size-12 rounded-md bg-slate-100 shrink-0">
                      <img src={item.img} alt="" className="size-12" loading="lazy" />
                    </div>
                    <div className="overflow-hidden grow">
                      <div className="ltr:float-right rtl:float-left">
                        <button
                          className="transition-all duration-150 ease-linear text-slate-500 dark:text-zink-200 hover:text-red-500 dark:hover:text-red-500"
                          onClick={() => removeItem(item.id, item.properties)}
                        >
                          <X className="size-4"></X>
                        </button>
                      </div>
                      <a
                        href="#!"
                        className="transition-all duration-200 ease-linear hover:text-custom-500"
                      >
                        <h6 className="mb-1 text-base line-clamp-2">
                          {item.title}
                        </h6>
                      </a>
                      <div className="flex items-center mb-3">
                        <h5 className="text-base product-price text-common">
                          $<span>{convertPrice(item.price, exChangeRate)}</span>
                        </h5>
                      </div>
                      <div className="flex items-center justify-between gap-3">
                        <div className="inline-flex text-center input-step">
                          <button
                            type="button"
                            className="transition-all size-8 duration-200 ease-linear bg-white border hover:bg-common hover:text-white"
                            onClick={() => countDown(item.id, item.properties)}
                          >
                            <Minus className="inline-block size-4"></Minus>
                          </button>
                          <span className='w-12 h-8 flex items-center justify-center pointer-events-none border-y product-quantity'>{item.quantity}</span>
                          <button
                            type="button"
                            className="transition-all size-8 duration-200 ease-linear bg-white border hover:bg-common hover:text-white"
                            onClick={() => countUP(item.id, item.properties)}
                          >
                            <Plus className="inline-block size-4"></Plus>
                          </button>
                        </div>
                        <h6 className="product-line-price text-common">
                          $
                          {convertPrice(
                            item.quantity * item.price,
                            exChangeRate
                          )}
                        </h6>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="p-4 border-t border-slate-200 dark:border-zink-500">
              <table className="w-full mb-3 ">
                <tbody className="table-total">
                  {/* <tr>
                  <td className="py-2">Sub Total :</td>
                  <td className="text-right cart-subtotal">
                    ${convertPrice(subTotal)}
                  </td>
                </tr> */}
                  {/* <tr>
                  <td className="py-2">
                    Discount<span className="text-muted"></span>:
                  </td>
                  <td className="text-right cart-discount">
                    {dis ? `-${dis.toFixed(2)}` : 0}
                  </td>
                </tr>
                <tr>
                  <td className="py-2">Shipping Charge :</td>
                  <td className="text-right cart-shipping">0</td>
                </tr> */}
                  <tr className="font-semibold">
                    <td className="py-2">{t('Total')} : </td>
                    <td className="text-right cart-total text-common">
                      ${convertPrice(subTotal, exChangeRate)}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <p className="p-4">{t('Empty Cart')}</p>
        )}
        <div className="p-4 flex items-center justify-between gap-3">
          <Link
            to="/"
            onClick={handleDrawer}
            className="w-full text-white btn bg-slate-500 border-slate-500 hover:text-white hover:bg-slate-600 hover:border-slate-600 focus:text-white focus:bg-slate-600 focus:border-slate-600 focus:ring focus:ring-slate-100 active:text-white active:bg-slate-600 active:border-slate-600 active:ring active:ring-slate-100 dark:ring-slate-400/10"
          >
            {t('Continue Shopping')}
          </Link>
          <Link
            to="/ecommerce-checkout"
            onClick={handleDrawer}
            className={`w-full text-white bg-common btn ${Boolean(counts) ? '' : 'pointer-events-none'}`}
          >
            {t('Checkout')}
          </Link>
        </div>
      </Drawer>
    </React.Fragment>
  );
};

export default CartDrawer;
